import type { FetchPageServiceParameters } from "~/core/composables/useFetchPageV2";
import {
  DEFAULT_FIRST_PAGE,
  DEFAULT_PAGE_SIZE,
} from "~/core/constants/constants";
import type LxcError from "~/core/models/LxcError.class";
import type { PaginatedList } from "~/core/models/PaginatedList.interface";
import type {
  FilterSelectionValue,
  FiltersSelection,
} from "~/core/models/filters";
import RestService from "~/core/services/rest.service";
import businessUnitService from "~/modules/businessUnit/services/businessUnit.service";
import type { AsyncLog } from "~/modules/log/models/AsyncLog.interface";
import type { TranslatedLogAttribute } from "~/modules/log/models/TranslatedLogAttribute.interface";
import organizationService from "~/modules/organization/services/organization.service";
import superAdministrationService from "~/modules/superAdministration/services/superAdministration.service";

export class LogsService extends RestService {
  protected AUDIT_LOGS = "auditlogs";
  protected LOGS = "logs";
  protected ACTIONS = "actions";
  protected ACTION_TYPES = "actionTypes";

  getSuperAdministrationLogs({
    page,
    pageSize,
    searchParams,
    sort,
  }: FetchPageServiceParameters): Promise<PaginatedList<AsyncLog>> {
    const iterator = (searchParams as FiltersSelection).entries();
    const searchObject: Record<string, FilterSelectionValue> = {};

    for (const entry of iterator) {
      const filterDefinition = entry[1];
      searchObject[filterDefinition.key as string] = filterDefinition.value;
    }

    const params = { page, pageSize, ...searchObject, sort };

    return this.$apiV2.get(
      `${this.getSuperAdministrationAuditLogsPath()}/${this.LOGS}`,
      {
        headers: { Accept: "application/json" },
        params,
        timeout: Infinity,
      },
    );
  }

  getBusinessUnitLogs({
    businessUnitCode,
    page,
    pageSize,
    searchParams,
    sort,
  }: FetchPageServiceParameters): Promise<PaginatedList<AsyncLog>> {
    const iterator = (searchParams as FiltersSelection).entries();
    const searchObject: Record<string, FilterSelectionValue> = {};

    for (const entry of iterator) {
      const filterDefinition = entry[1];
      searchObject[filterDefinition.key as string] = filterDefinition.value;
    }

    const params = { page, pageSize, ...searchObject, sort };

    return this.$apiV2.get(
      `${this.getBusinessUnitAuditLogsPath(businessUnitCode)}/${this.LOGS}`,
      {
        headers: { Accept: "application/json" },
        params,
        timeout: Infinity,
      },
    );
  }

  getOrganizationLogs({
    organizationCode,
    page,
    pageSize,
    searchParams,
    sort,
  }: FetchPageServiceParameters): Promise<PaginatedList<AsyncLog>> {
    const iterator = (searchParams as FiltersSelection).entries();
    const searchObject: Record<string, FilterSelectionValue> = {};

    for (const entry of iterator) {
      const filterDefinition = entry[1];
      searchObject[filterDefinition.key as string] = filterDefinition.value;
    }

    const params = { page, pageSize, ...searchObject, sort };

    return this.$apiV2.get(
      `${this.getOrganizationAuditLogsPath(organizationCode)}/${this.LOGS}`,
      {
        headers: { Accept: "application/json" },
        params,
        timeout: Infinity,
      },
    );
  }

  async getSuperAdministrationActions({
    page,
    pageSize,
    searchParams,
  }: FetchPageServiceParameters): Promise<
    PaginatedList<TranslatedLogAttribute> | LxcError
  > {
    const params = {
      page,
      pageSize,
      filter: searchParams,
    };

    return this.$apiV2.get(
      `${this.getSuperAdministrationAuditLogsPath()}/${this.ACTIONS}`,
      {
        params,
      },
    );
  }

  async getBusinessUnitActions({
    businessUnitCode,
    page,
    pageSize,
    searchParams,
  }: FetchPageServiceParameters): Promise<
    PaginatedList<TranslatedLogAttribute> | LxcError
  > {
    const params = {
      page,
      pageSize,
      filter: searchParams,
    };

    return this.$apiV2.get(
      `${this.getBusinessUnitAuditLogsPath(businessUnitCode)}/${this.ACTIONS}`,
      {
        params,
      },
    );
  }

  async getOrgnizationActions({
    organizationCode,
    page,
    pageSize,
    searchParams,
  }: FetchPageServiceParameters): Promise<
    PaginatedList<TranslatedLogAttribute> | LxcError
  > {
    const params = {
      page,
      pageSize,
      filter: searchParams,
    };

    return this.$apiV2.get(
      `${this.getOrganizationAuditLogsPath(organizationCode)}/${this.ACTIONS}`,
      {
        params,
      },
    );
  }

  async getSuperAdministrationActionTypes({
    page,
    pageSize,
    searchParams,
  }: FetchPageServiceParameters): Promise<
    PaginatedList<TranslatedLogAttribute> | LxcError
  > {
    const params = {
      page: page ?? DEFAULT_FIRST_PAGE,
      pageSize: pageSize ?? DEFAULT_PAGE_SIZE,
      filter: searchParams,
    };

    return this.$apiV2.get(
      `${this.getSuperAdministrationAuditLogsPath()}/${this.ACTION_TYPES}`,
      { params },
    );
  }

  async getBusinessUnitActionTypes({
    businessUnitCode,
    page,
    pageSize,
    searchParams,
  }: FetchPageServiceParameters): Promise<
    PaginatedList<TranslatedLogAttribute> | LxcError
  > {
    const params = {
      page: page ?? DEFAULT_FIRST_PAGE,
      pageSize: pageSize ?? DEFAULT_PAGE_SIZE,
      filter: searchParams,
    };

    return this.$apiV2.get(
      `${this.getBusinessUnitAuditLogsPath(businessUnitCode)}/${this.ACTION_TYPES}`,
      { params },
    );
  }

  async getOrganizationActionTypes({
    organizationCode,
    page,
    pageSize,
    searchParams,
  }: FetchPageServiceParameters): Promise<
    PaginatedList<TranslatedLogAttribute> | LxcError
  > {
    const params = {
      page: page ?? DEFAULT_FIRST_PAGE,
      pageSize: pageSize ?? DEFAULT_PAGE_SIZE,
      filter: searchParams,
    };

    return this.$apiV2.get(
      `${this.getOrganizationAuditLogsPath(organizationCode)}/${this.ACTION_TYPES}`,
      { params },
    );
  }

  private getSuperAdministrationAuditLogsPath(): string {
    return `${superAdministrationService.getSuperAdministrationPath()}/${this.AUDIT_LOGS}`;
  }

  private getBusinessUnitAuditLogsPath(businessUnitCode?: number): string {
    return `${businessUnitService.getBusinessUnitPath(businessUnitCode)}/${this.AUDIT_LOGS}`;
  }

  private getOrganizationAuditLogsPath(organizationCode?: number): string {
    return `${organizationService.getOrganizationPath(organizationCode)}/${this.AUDIT_LOGS}`;
  }
}

export default new LogsService();
